import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Copy, Loader2, Printer } from "lucide-react";
import { toast } from "sonner";
import { getGiftVoucher } from "@/lib/gift.functions";
import { GIFT_ART } from "@/components/shop/GiftMemberships";
import type { GiftMonths } from "@/lib/gift-plans";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/gift/voucher/$code")({
  head: () => ({
    meta: [
      { title: "Your Gift Voucher | Recovery Trauma™" },
      { name: "description", content: "Print or save your Recovery Trauma™ Gift Membership voucher." },
      { property: "og:title", content: "Gift Voucher | Recovery Trauma™" },
      { property: "og:description", content: "A Recovery Trauma™ Gift Membership voucher." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: VoucherPage,
});

function VoucherPage() {
  const { code } = Route.useParams();
  const fetchV = useServerFn(getGiftVoucher);
  const { data, isLoading } = useQuery({ queryKey: ["gift-voucher", code], queryFn: () => fetchV({ data: { code } }) });
  if (isLoading) return <main className="flex min-h-[50vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" aria-label="Loading" /></main>;
  const v = data?.voucher;
  if (!v) return <main className="mx-auto max-w-xl px-4 py-16 text-center"><h1 className="text-2xl font-semibold">Voucher not found</h1><p className="mt-2 text-muted-foreground">Please check the code in your email.</p></main>;
  const months = v.plan_months as GiftMonths;
  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-8 print:p-0">
      <div className="mb-4 flex flex-wrap justify-end gap-2 print:hidden">
        <Button
          variant="outline"
          className="rounded-full"
          onClick={async () => {
            try { await navigator.clipboard.writeText(v.code); toast.success("Gift code copied"); }
            catch { toast.error("Couldn't copy — press and hold the code to copy it."); }
          }}
        ><Copy className="me-2 h-4 w-4" aria-hidden /> Copy gift code</Button>
        <Button onClick={() => window.print()} className="rounded-full"><Printer className="me-2 h-4 w-4" aria-hidden /> Print or save as PDF</Button>
      </div>
      <article className="overflow-hidden rounded-3xl border-2 border-[var(--gold-deep)]/50 bg-card print:break-inside-avoid">
        <div className="relative aspect-[8/5] w-full">
          <img src={GIFT_ART[months]} alt="" className="h-full w-full object-cover" />
          {months === 12 ? null : (
            <div className="absolute inset-0 flex flex-col items-end justify-center pe-[7%] text-end">
              <span className="text-xs font-semibold uppercase tracking-[0.3em] text-[var(--gold-deep)]">Gift Membership</span>
              <span className="font-display text-4xl font-semibold sm:text-6xl">{months} Months</span>
              <span data-no-translate className="text-sm text-foreground/70">Recovery Trauma™ · Healing Beyond Survival Mode™</span>
            </div>
          )}
        </div>
        <div className="space-y-3 p-6 text-center">
          {v.recipient_name ? <p className="font-display text-xl">For {v.recipient_name}</p> : null}
          {v.message ? <p className="mx-auto max-w-lg whitespace-pre-line italic text-foreground/80">“{v.message}”</p> : null}
          {v.buyer_name ? <p className="text-sm text-muted-foreground">With love from {v.buyer_name}</p> : null}
          <p className="pt-2 text-sm text-muted-foreground">Your gift code</p>
          <p className="font-mono text-2xl font-bold tracking-widest sm:text-3xl">{v.code}</p>
          <p className="text-sm">Redeem at <strong>www.recoverytrauma.com/redeem-gift</strong></p>
          <p className="text-xs text-muted-foreground">Membership starts on the day it's redeemed. One-time gift — it never renews.</p>
          {v.redeemed ? <p className="text-sm font-semibold text-[var(--gold-deep)]">This voucher has been redeemed.</p> : null}
        </div>
      </article>
      <p className="mt-6 text-center print:hidden">
        <Link to="/redeem-gift" search={{ code: v.code }} className="text-[var(--gold-deep)] underline">Redeem this gift now</Link>
      </p>
    </main>
  );
}
